import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAllProjectsSuperAdmin } from "../../services/service";
import DataGrid from "../../atoms/DataTable/DataGrid";
import ListHeader from "../../molecules/ListHeader/ListHeader";

const AllProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const rowsPerPageRef = useRef(rowsPerPage);

  useEffect(() => {
    rowsPerPageRef.current = rowsPerPage;
  }, [rowsPerPage]);

  const fetchProjects = useCallback(async (page = 1, limit, overrides = {}) => {
    const effectiveLimit = limit ?? rowsPerPageRef.current;
    const statusValue =
      overrides.status !== undefined ? overrides.status : statusFilter;
    const searchValue =
      overrides.search !== undefined ? overrides.search : debouncedSearch;
    const searchParam = searchValue?.trim() || undefined;

    setLoading(true);
    try {
      // Fetching all (up to 1000) because "inactive" state filtering is not properly handled by the backend
      const response = await getAllProjectsSuperAdmin(1, 1000, {
        search: searchParam,
      });

      if (response?.success) {
        let finalData = response.data || [];

        // Apply local filtering for status
        if (statusValue && statusValue !== "all") {
          finalData = finalData.filter((p) => {
            const currentStatus = (p.project_status || "").toLowerCase();
            return currentStatus === statusValue.toLowerCase();
          });
        }

        // Apply local pagination
        const totalItems = finalData.length;
        const startIndex = (page - 1) * effectiveLimit;
        const endIndex = startIndex + effectiveLimit;
        const paginatedData = finalData.slice(startIndex, endIndex);

        setProjects(paginatedData);
        setRowsPerPage(effectiveLimit);
        setPagination({
          current: page,
          pageSize: effectiveLimit,
          total: totalItems,
        });
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
    setLoading(false);
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchProjects(1, rowsPerPageRef.current, {
      status: statusFilter,
      search: debouncedSearch,
    });
  }, [fetchProjects, statusFilter, debouncedSearch]);

  const handlePageChange = (newPage, newPageSize) => {
    setRowsPerPage(newPageSize);
    setPagination((prev) => ({
      ...prev,
      current: newPage,
      pageSize: newPageSize,
    }));
    fetchProjects(newPage, newPageSize, {
      status: statusFilter,
      search: debouncedSearch,
    });
  };

  const handleSearchInputChange = (value = "") => {
    setSearchTerm(value);
  };

  const handleFilterChange = (values = {}) => {
    const status = values?.status || "all";
    setStatusFilter(status);
  };

  return (
    <div className="row px-[5px] py-4">
      <div className="col-12">
        <div className="row d-flex align-items-end">
          <div className="col-12">
            <ListHeader
              mainHeading="All Projects"
              placeholder="Search by project, client, location, or organization"
              handleSearchChange={handleFilterChange}
              linkBreadCrum="/super-admin/all-projects"
              blinkBreadCrumText="Projects"
              blinkBreadCrumText1="All Projects"
              searchShow={true}
              searchValue={searchTerm}
              handleProjectSearchChange={handleSearchInputChange}
              moduleType="super-admin-projects"
              showFilter={true}
              DropDown={false}
            />
          </div>
        </div>
      </div>

      <div className="w-full px-2 pt-4">
        <DataGrid
          index={3}
          loader={loading}
          data={projects}
          pagination={pagination}
          onPageChange={handlePageChange}
          getData={fetchProjects}
          isSuperAdmin={true}
        />
      </div>
    </div>
  );
};

export default AllProjects;

